/**
 * The board picker anchors itself with a `position: fixed` span whose `top` /
 * `left` are written from a trigger's viewport rect. That only lands where it
 * was told to while the span's containing block is the viewport — an ancestor
 * that captures fixed descendants (a transform, a filter, containment) makes
 * the same coordinates resolve against its own box instead, displacing the
 * popover by that box's origin.
 *
 * The captor is measured, not inferred from computed styles. This module used
 * to carry a style-sniffing heuristic, and it kept being wrong in both
 * directions: `content-visibility: auto` captures without touching the
 * computed `transform`, and `container-type: inline-size` reads in the
 * containment spec as though it captures but does not in Chrome — which had
 * the tile media misidentified as the captor while a reveal-animation
 * `transform` further out was the real one, and put the network tab's board
 * picker in the far corner of the viewport. Writing `0,0` and reading where
 * the span lands yields the containing block's padding-box origin — the exact
 * point fixed offsets resolve against — whatever CSS produced it.
 */

/**
 * Where to write a fixed element's `top` / `left` so it lands on `targetRect`
 * in viewport coordinates, whatever captures it.
 *
 * Probes by writing `0,0` onto the anchor; the caller is expected to write
 * the returned offsets straight back, so the probe values never survive a
 * frame.
 *
 * A captor that scales or rotates rather than translating is still only
 * approximated: `getBoundingClientRect` reports its post-transform box while
 * the offsets written here resolve pre-transform. Translation — the case
 * carousels and reveal animations produce — is exact.
 */
export const resolveFixedOffset = (
    anchor: HTMLElement,
    targetRect: DOMRect,
): { top: number; left: number } => {
    anchor.style.top = '0px';
    anchor.style.left = '0px';
    const origin = anchor.getBoundingClientRect();
    return {
        top: targetRect.top - origin.top,
        left: targetRect.left - origin.left,
    };
};
